import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileCheck2, AlertTriangle, ShieldCheck, Clock, IdCard, GraduationCap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

const STATUS_STYLES = {
  pending: { bg: "bg-amber-50 text-amber-800 border-amber-200", icon: <Clock className="w-3.5 h-3.5" />, label: "Pending review" },
  verified: { bg: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: <ShieldCheck className="w-3.5 h-3.5" />, label: "Verified" },
  rejected: { bg: "bg-rose-50 text-rose-800 border-rose-200", icon: <AlertTriangle className="w-3.5 h-3.5" />, label: "Rejected" },
};

export default function CredentialWallet({ user, onChanged }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/credentials/mine");
      setDocs(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const status = user?.credential_status || "pending";
  const s = STATUS_STYLES[status];
  const byKind = (k) => docs.find(d => d.kind === k);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Credential wallet</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">Get verified to unlock shifts</h2>
        </div>
        <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-medium ${s.bg}`} data-testid="verification-badge">
          {s.icon}{s.label}
        </span>
      </div>

      {status === "rejected" && user.rejection_reason && (
        <Card className="border-rose-200 bg-rose-50/60 p-4 text-sm text-rose-800 rounded-2xl">
          <strong className="font-semibold">Admin note:</strong> {user.rejection_reason}
        </Card>
      )}

      <Card className="rounded-2xl border-slate-200 p-6 space-y-4 bg-white">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Registration</div>
        <div className="flex items-baseline justify-between">
          <span className="font-display text-lg font-semibold">Council registration number</span>
          <span className="text-slate-800 font-mono text-sm">{user?.council_registration_number || "—"}</span>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-5">
        <UploadSlot testid="upload-degree" kind="degree" title="Degree certificate" desc="MBBS / BDS / GNM / BSc Nursing etc." icon={<GraduationCap className="w-5 h-5" />} existing={byKind("degree")} onDone={async () => { await load(); await onChanged?.(); }} />
        <UploadSlot testid="upload-govid" kind="government_id" title="Government ID" desc="Aadhaar / PAN / Driving Licence" icon={<IdCard className="w-5 h-5" />} existing={byKind("government_id")} onDone={async () => { await load(); await onChanged?.(); }} />
      </div>

      {status === "verified" && (
        <Card className="rounded-2xl border-emerald-200 bg-emerald-50/60 p-5 text-sm text-emerald-900">
          <div className="flex items-center gap-2 font-semibold"><ShieldCheck className="w-4 h-4" /> You&apos;re verified.</div>
          <p className="mt-1 text-emerald-900/80">Head to the Browse tab to find and apply to open shifts across Pune.</p>
        </Card>
      )}

      {!loading && docs.length < 2 && (
        <div className="text-sm text-slate-500">Upload both documents to complete your submission.</div>
      )}
    </div>
  );
}

function UploadSlot({ kind, title, desc, icon, existing, onDone, testid }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await api.post("/credentials/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${title} uploaded`);
      await onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <Card className="rounded-2xl border-slate-200 p-6 bg-white space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center">{icon}</div>
          <div>
            <div className="font-display font-semibold">{title}</div>
            <div className="text-xs text-slate-500">{desc}</div>
          </div>
        </div>
        {existing && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><FileCheck2 className="w-3 h-3 mr-1" />Uploaded</Badge>}
      </div>
      <div className="border-2 border-dashed border-slate-200 rounded-xl p-5 text-center bg-slate-50/50">
        {existing ? (
          <div className="text-sm text-slate-700 truncate" title={existing.file_name}>{existing.file_name}</div>
        ) : (
          <div className="text-sm text-slate-500">PDF, JPG or PNG · max 10MB</div>
        )}
        <input ref={inputRef} data-testid={testid} type="file" accept=".pdf,image/jpeg,image/png" className="hidden" onChange={handle} />
        <Button type="button" disabled={busy} onClick={() => inputRef.current?.click()} className="mt-4 rounded-full bg-emerald-700 hover:bg-emerald-800">
          <UploadCloud className="w-4 h-4 mr-1.5" />
          {busy ? "Uploading…" : existing ? "Replace file" : "Upload"}
        </Button>
      </div>
    </Card>
  );
}
